import { defineStore } from 'pinia'
import { apiService } from '@/services/api'
import { formatDate } from '@/components/logics/dateUtil'
import { useUserStore } from '@/stores/userStore'

export const useSubsidyStore = defineStore('subsidy', {
  state: () => ({
    // 削除判定フラグ
    isDeletedCompany: false,
    isDeletedBranch: false,
    isDeletedStaff: false,
    // 助成金リスト
    subsidies: [],

    // テーブル表示用リスト定義
    tableList: [],

    // 編集用フォームデータ
    formData: null,

    // フォームの初期値
    formInit: {
      id: null,
      contractId: null,
      subsidyType: '',
      status: 'plan_creating',
      companyId: null,
      branchId: null,
      staffId: null,
      memo: '',
      paymentApplicationDeadline: null,
      dateJson: {
        applicationStartDate: {
          isChecked: false,
          date: null,
          userId: null,
          userName: '',
        },
        submissionDate: {
          isChecked: false,
          date: null,
          userId: null,
          userName: '',
        },
        paymentStartDate: {
          isChecked: false,
          date: null,
          userId: null,
          userName: '',
        },
        salaryIncreaseDate: {
          isChecked: false,
          date: null,
          userId: null,
          userName: '',
        },
        joinDate: {
          isChecked: false,
          date: null,
          userId: null,
          userName: '',
        },
        applicableDate: {
          isChecked: false,
          date: null,
          userId: null,
          userName: '',
        },
        planStartDate: {
          isChecked: false,
          date: null,
          userId: null,
          userName: '',
        },
        planEndDate: {
          isChecked: false,
          date: null,
          userId: null,
          userName: '',
        },
      },
      colorInfo: {
        subsidyType: '',
        companyId: '',
        branchId: '',
        staffId: '',
        applicableDate: '',
        planStartDate: '',
        planEndDate: '',
        memo: '',
        applicationStartDate: '',
        paymentApplicationDeadline: '',
        submissionDate: '',
        paymentStartDate: '',
        salaryIncreaseDate: '',
        joinDate: '',
      },
    },

    // 軽量な選択肢データ
    companyNames: [],
    branchNames: [],
    staffNames: [],

    // 設定情報
    config: {},

    // ステータスマッピング定数
    STATUS_MAP: {
      plan_creating: '【計】作成中',
      plan_submitted: '【計】提出完了',
      plan_fixing: '【計】不備対応中',
      payment_creating: '【支】作成中',
      payment_submitted: '【支】提出完了',
      payment_fixing: '【支】不備対応中',
      in_planning_period: '計画期間中',
      completed: '完了',
      confirmed: '確認',
      payment_received: '入金連絡有',
    },
  }),

  actions: {
    // テーブル表示用のリストを初期化
    initTableList() {
      this.tableList = [
        {
          label: 'No',
          key: 'rowNumber',
          data: (item, value) => value,
        },
        {
          label: '助成金種類',
          key: 'subsidyType',
          data: item => item.subsidyType || '-',
        },
        {
          label: '会社',
          key: 'companyId',
          data: (item, value) => item.companyName || '削除された会社',
        },
        {
          label: '事業場',
          key: 'branchId',
          data: (item, value) => item.branchName || '削除された事業場',
        },
        {
          label: '従業員',
          key: 'staffId',
          data: (item, value) => item.staffName || '削除された従業員',
        },
        {
          label: '状況',
          key: 'status',
          data: (item, value) => this.getStatusLabel(item.status),
          tag: {
            '【計】作成中': 'budget-creation-in-progress',
            '【計】提出完了': 'budget-submitted',
            '【計】不備対応中': 'budget-revision-in-progress',
            '【支】作成中': 'payment-creation-in-progress',
            '【支】提出完了': 'payment-submitted',
            '【支】不備対応中': 'payment-revision-in-progress',
            計画期間中: 'planning-period',
            完了: 'completed',
            確認: 'awaiting-confirmation',
            入金連絡有: 'payment-received',
          },
          tag_css: 'wide',
        },
        {
          label: '支給申請期限',
          key: 'paymentApplicationDeadline',
          data: item => (item.paymentApplicationDeadline ? formatDate(item.paymentApplicationDeadline, '-') : '-'),
        },
        {
          label: '申請開始日',
          key: 'applicationStartDate',
          data: item => this.getDateFromJson(item, 'applicationStartDate'),
        },
        {
          label: '提出日、最終更新日',
          key: 'submissionDate',
          data: item => this.getDateFromJson(item, 'submissionDate'),
        },
        {
          label: '支給開始日',
          key: 'paymentStartDate',
          data: item => this.getDateFromJson(item, 'paymentStartDate'),
        },
        {
          label: '昇給日',
          key: 'salaryIncreaseDate',
          data: item => this.getDateFromJson(item, 'salaryIncreaseDate'),
        },
        {
          label: '入社日',
          key: 'joinDate',
          data: item => this.getDateFromJson(item, 'joinDate'),
        },
        {
          label: '該当日',
          key: 'applicableDate',
          data: item => this.getDateFromJson(item, 'applicableDate'),
        },
        {
          label: '計画期間開始日',
          key: 'planStartDate',
          data: item => this.getDateFromJson(item, 'planStartDate'),
        },
        {
          label: '計画期間終了日',
          key: 'planEndDate',
          data: item => this.getDateFromJson(item, 'planEndDate'),
        },
        {
          label: '備考',
          key: 'memo',
          data: item => item.memo || '-',
        },
      ]
    },

    // ステータス値から表示ラベルを取得
    getStatusLabel(status) {
      return this.STATUS_MAP[status] || status
    },

    // ステータスラベルから値を取得
    getStatusValueFromLabel(label) {
      const entry = Object.entries(this.STATUS_MAP).find(([key, value]) => value === label)
      return entry ? entry[0] : label
    },

    // JSONから日付を取得するヘルパー
    getDateFromJson(item, category) {
      if (!item.dateJson || !item.dateJson[category]) return '-'
      const dateValue = item.dateJson[category].date
      if (!dateValue || dateValue === '1970-01-11') return '-'
      return formatDate(dateValue, '/')
    },

    // 助成金一覧取得API呼び出し
    async getSubsidies() {
      try {
        const response = await apiService.get('api/subsidies')

        this.subsidies = response.subsidies || []

        // JSONを整形
        this.subsidies = this.subsidies.map(item => this.parseJsonFields(item))

        return true
      } catch (error) {
        return false
      }
    },

    // 助成金詳細取得API呼び出し
    async getSubsidyDetail(id) {
      if (!id) return null
      try {
        const response = await apiService.get(`/api/subsidies/${id}`)
        if (response.subsidy) {
          // JSON変換を行ってからディープコピーしてフォームデータに設定
          const parsedSubsidy = this.parseJsonFields(response.subsidy)
          this.formData = JSON.parse(JSON.stringify(parsedSubsidy))
          return this.formData
        }
        return null
      } catch (error) {
        return null
      }
    },

    // 会社名リストを取得
    async getCompanyNames() {
      try {
        const response = await apiService.get('/api/companies/names')
        this.companyNames = response.companies || []
        return this.companyNames
      } catch (error) {
        this.companyNames = []
        return []
      }
    },

    // 会社IDで支店名リストを取得
    async getBranchNamesByCompanyId(companyId) {
      try {
        const response = await apiService.get(`/api/companies/${companyId}/branch-names`)
        this.branchNames = response.branches || []
        return this.branchNames
      } catch (error) {
        this.branchNames = []
        return []
      }
    },

    // 支店IDでスタッフ名リストを取得
    async getStaffNamesByBranchId(branchId) {
      try {
        const response = await apiService.get(`/api/branches/${branchId}/staff-names`)
        this.staffNames = response.staff || []
        return this.staffNames
      } catch (error) {
        this.staffNames = []
        return []
      }
    },

    // 編集フォームデータの設定
    async setFormData(item = null) {
      // 削除フラグ初期化
      this.isDeletedCompany = false
      this.isDeletedBranch = false
      this.isDeletedStaff = false

      if (item && item.id) {
        await this.getSubsidyDetail(item.id)

        // 会社リスト取得
        await this.getCompanyNames()
        // 会社が削除されているか判定
        this.isDeletedCompany = item.companyId === null

        // 事業場リスト取得
        if (this.formData.companyId) {
          await this.getBranchNamesByCompanyId(this.formData.companyId)
          // 事業場が削除されているか判定
          this.isDeletedBranch = item.branchId === null
        } else {
          this.branchNames = []
        }

        // 従業員リスト取得
        if (this.formData.branchId) {
          await this.getStaffNamesByBranchId(this.formData.branchId)
          // 従業員が削除されているか判定
          this.isDeletedStaff = item.staffId === null
        } else {
          this.staffNames = []
        }

        // 日付JSONの整形
        if (this.formData.dateJson) {
          for (const key in this.formData.dateJson) {
            const dateInfo = this.formData.dateJson[key]
            if (dateInfo.date) {
              dateInfo.date = formatDate(dateInfo.date, '-')
            }
            // チェックボックスの初期化（1970-01-11の場合はチェックを外す）
            if (dateInfo.date === '1970-01-11') {
              dateInfo.isChecked = false
              dateInfo.date = ''
            } else if (dateInfo.date) {
              dateInfo.isChecked = true
            }
          }
        }

        // 支給申請期限日のフォーマット処理
        if (this.formData.paymentApplicationDeadline) {
          this.formData.paymentApplicationDeadline = formatDate(this.formData.paymentApplicationDeadline, '-')
        }
      } else {
        // 新規作成の場合は初期値を使用
        this.formData = JSON.parse(JSON.stringify(this.formInit))
        this.branchNames = []
        this.staffNames = []
        this.isDeletedCompany = false
        this.isDeletedBranch = false
        this.isDeletedStaff = false
      }
    },

    // 会社セレクトの状態を返す
    getCompanySelectState(isEdit = false) {
      const formData = this.formData
      if (!formData) return null
      const companyId = formData.companyId
      if (isEdit && this.isDeletedCompany) return 'deleted'
      if (companyId === null) return 'select'
      return null
    },

    // 事業場セレクトの状態を返す
    getBranchSelectState(isEdit = false) {
      const formData = this.formData
      if (!formData) return 'select'
      const branchId = formData.branchId
      const companyId = formData.companyId
      if (!companyId) return null
      if (isEdit && this.isDeletedBranch) return 'deleted'
      if (branchId === null && companyId) return 'select'
      return null
    },

    // 従業員セレクトの状態を返す
    getStaffSelectState(isEdit = false) {
      const formData = this.formData
      if (!formData) return 'select'

      const staffId = formData.staffId
      const branchId = formData.branchId

      // 事業場が選択されていない場合、従業員も選択できない
      if (!branchId) {
        return null // disabled状態
      }

      // 編集モードで、削除された従業員フラグがtrueの場合
      if (isEdit && this.isDeletedStaff) {
        return 'deleted'
      }

      // staffIdがnullで事業場が選択されている場合は「選択」状態
      if (staffId === null && branchId) {
        return 'select'
      }

      return null // 正常に選択されている状態
    },

    // 会社変更時の処理
    async onCompanyChange() {
      if (!this.formData) return

      // 事業場と従業員をリセット
      this.formData.branchId = null
      this.formData.staffId = null
      // 削除フラグをリセット
      this.isDeletedCompany = false
      this.isDeletedBranch = false
      this.isDeletedStaff = false

      // 事業場オプションを取得
      await this.getBranchNamesByCompanyId(this.formData.companyId)
      this.staffNames = []
    },

    // 事業場変更時の処理
    async onBranchChange() {
      if (!this.formData) return

      // 従業員をリセット
      this.formData.staffId = null
      // 削除フラグをリセット
      this.isDeletedBranch = false
      this.isDeletedStaff = false

      // 従業員オプションを取得
      await this.getStaffNamesByBranchId(this.formData.branchId)
    },

    // チェックボックス変更時の処理
    updateCheckboxState(category) {
      if (!this.formData?.dateJson?.[category]) return

      const checkItem = this.formData.dateJson[category]

      if (checkItem.isChecked) {
        // // チェックが入った場合：現在日時を設定
        // const userStore = useUserStore()
        // checkItem.date = formatDate(new Date(), '-')
        // checkItem.userId = userStore.currentUserId
        // checkItem.userName = userStore.currentUserShortName
      } else {
        // チェックが外された場合：チェック情報をクリア
        checkItem.date = ''
        checkItem.userId = null
        checkItem.userName = ''
      }
    },

    // 日付変更時の処理
    updateDateInfo(category, date) {
      if (!this.formData?.dateJson?.[category]) return

      const dateItem = this.formData.dateJson[category]

      // 日付が空の場合はチェックを外す
      if (!date) {
        dateItem.date = ''
        dateItem.userId = null
        dateItem.userName = ''
      } else {
        const userStore = useUserStore()
        dateItem.date = formatDate(date, '-')
        dateItem.userId = userStore.currentUserId
        dateItem.userName = userStore.currentUserShortName
      }
    },

    // 助成金保存API呼び出し
    async saveSubsidy() {
      try {
        // ディープコピーを作成
        const saveData = JSON.parse(JSON.stringify(this.formData))

        // JSONフィールドを文字列に変換
        saveData.dateJson = JSON.stringify(saveData.dateJson)
        saveData.colorInfo = JSON.stringify(saveData.colorInfo)

        const response = await apiService.post('/api/subsidies', saveData)

        // 一覧を再取得
        await this.getSubsidies()
        return response.message || '助成金データを登録しました。'
      } catch (error) {
        return null
      }
    },

    // 助成金削除API呼び出し
    async deleteSubsidy() {
      if (!this.formData || !this.formData.id) return null

      try {
        const response = await apiService.delete(`/api/subsidies/${this.formData.id}`)

        // 一覧を再取得
        await this.getSubsidies()
        return response.message || '助成金データを削除しました。'
      } catch (error) {
        return null
      }
    },

    // コピー処理
    copySubsidy() {
      if (!this.formData) return false

      // IDをリセットして新規として扱う
      this.formData.id = null

      return true
    },

    // 共通JSON変換処理
    parseJsonFields(item) {
      if (!item) return item

      const parsedItem = { ...item }

      try {
        // dateJsonを文字列からオブジェクトに変換
        if (typeof parsedItem.dateJson === 'string') {
          parsedItem.dateJson = JSON.parse(parsedItem.dateJson)
        }
      } catch (error) {
        parsedItem.dateJson = JSON.parse(JSON.stringify(this.formInit.dateJson))
      }

      try {
        // colorInfoを文字列からオブジェクトに変換
        if (typeof parsedItem.colorInfo === 'string') {
          parsedItem.colorInfo = JSON.parse(parsedItem.colorInfo)
        }
      } catch (error) {
        parsedItem.colorInfo = JSON.parse(JSON.stringify(this.formInit.colorInfo))
      }

      return parsedItem
    },
  },

  getters: {
    // ステータスオプションを取得
    statusOptions(state) {
      return Object.entries(state.STATUS_MAP).map(([value, label]) => ({
        value,
        label,
      }))
    },

    statusLabelToValue: (state, getters) => label => {
      const option = state.statusOptions.find(opt => opt.label === label)
      return option ? option.value : label
    },

    // 会社名リストからセレクトボックス用の選択肢を生成
    companyNameOptions(state) {
      return state.companyNames.map(company => ({
        label: company.name,
        value: company.id,
      }))
    },

    // 事業場名リストからセレクトボックス用の選択肢を生成
    branchNameOptions(state) {
      return state.branchNames.map(branch => ({
        label: branch.name,
        value: branch.id,
      }))
    },

    // スタッフ名リストからセレクトボックス用の選択肢を生成
    staffNameOptions(state) {
      return state.staffNames.map(staff => ({
        label: staff.name,
        value: staff.id,
      }))
    },

    // ステータス別の件数
    statusCounts: state => {
      const counts = {
        total: state.subsidies.length,
        plan_creating: 0,
        plan_submitted: 0,
        plan_fixing: 0,
        payment_creating: 0,
        payment_submitted: 0,
        payment_fixing: 0,
        in_planning_period: 0,
        completed: 0,
        confirmed: 0,
        payment_received: 0,
      }

      state.subsidies.forEach(subsidy => {
        counts[subsidy.status] = (counts[subsidy.status] || 0) + 1
      })

      return counts
    },

    // チェック情報を取得するゲッター
    getCheckInfo: state => category => {
      if (!state.formData?.dateJson?.[category]?.date || !state.formData.dateJson[category].isChecked) return null
      const userStore = useUserStore()

      const userName = state.formData.dateJson[category].userName || '不明なユーザー'

      // ユーザーストアの省略表示メソッドを使用
      const truncatedName = userStore.getTruncatedUserName(userName, 22)

      return `${truncatedName}`
    },
  },
})
